/**
 * Solutions index page — grid of all 6 solutions.
 */
import { Link } from "react-router-dom";
import {
  Users2, Hammer, Home, Repeat, ScrollText, Shield,
  ArrowRight, Sparkles, ArrowUpRight,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "../MarketingChrome";

const SOLUTIONS = [
  {
    icon: Users2,
    slug: "wholesalers",
    persona: "Wholesalers",
    tagline: "Analyze faster. Assign more contracts.",
    desc: "Cut underwriting to 6 minutes. Rank verified buyers. Assign before the seller changes their mind.",
    primary: true,
  },
  {
    icon: Hammer,
    slug: "fix-and-flip",
    persona: "Fix & Flip",
    tagline: "Fund it. Fix it. Sell it — profitably.",
    desc: "Cited ARV, room-by-room repairs, and side-by-side exit comparison so no margin is left on the table.",
  },
  {
    icon: Home,
    slug: "buy-and-hold",
    persona: "Buy & Hold",
    tagline: "Underwrite for cash flow. Hold like a fund.",
    desc: "DSCR, cash-on-cash, and cap rate re-scored nightly. Maturity radar catches balloon dates before they surprise you.",
  },
  {
    icon: Repeat,
    slug: "brrrr",
    persona: "BRRRR",
    tagline: "Buy. Rehab. Rent. Refinance. Repeat.",
    desc: "BRRRR fit per ZIP. Refi radar per property. Only cycle where the math actually returns capital.",
  },
  {
    icon: ScrollText,
    slug: "creative-finance",
    persona: "Creative Finance",
    tagline: "Deeper margins. Structured on evidence.",
    desc: "Sub-To, Seller-Finance, and Novation scored against wholesale + flip with a numeric Why-Not audit.",
  },
  {
    icon: Shield,
    slug: "teams-enterprise",
    persona: "Teams & Enterprise",
    tagline: "Governance for real estate at scale.",
    desc: "6 roles × 26 capabilities. MFA. IP allowlist. Immutable audit. Explainable AI. Regulator-ready.",
  },
];

export default function SolutionsIndex() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="solutions-index">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              Six strategies. One operating system.
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              Whichever way<br />
              <span className="text-emerald-400">you make a deal.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              Wholesalers and funds run different playbooks — but the underlying engines are the same. Pick your strategy and see the exact workflow, tools, and metrics the platform surfaces for you.
            </p>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOLUTIONS.map(({ icon: Icon, slug, persona, tagline, desc, primary }) => (
            <Link
              key={slug}
              to={`/solutions/${slug}`}
              data-testid={`solution-card-${slug}`}
              className={`group relative rounded-2xl border p-6 transition-all hover:shadow-xl ${
                primary
                  ? "border-emerald-500 bg-emerald-50 hover:border-emerald-600"
                  : "border-slate-200 bg-white hover:border-emerald-500"
              }`}
            >
              <Icon className="w-8 h-8 text-emerald-600" />
              <div className="mt-6 font-bold text-xl group-hover:text-emerald-700">
                {persona}
              </div>
              <div className="mt-1 text-sm font-semibold text-emerald-700">
                {tagline}
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
              <div className="mt-6 text-sm font-semibold text-emerald-600 inline-flex items-center gap-1">
                See the playbook <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            One platform.<br />
            <span className="text-emerald-400">Every strategy. Every scale.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="solutions-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
